/**
 * Magic Wishlist — theme heart buttons (/a/magic-wishlist).
 */
(function () {
  'use strict';

  const GUEST_TOKEN_KEY = 'wishlist_guest_token';
  const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const DC_BUTTON_SELECTOR = '.dc-wishlist-heart-container .wishlist-heart-btn';

  function isValidGuestToken(token) {
    return typeof token === 'string' && UUID_RE.test(token.trim());
  }

  function getShopDomain() {
    const isMyshopify = (v) => typeof v === 'string' && /\.myshopify\.com$/i.test(v.trim());
    const candidates = [
      window.dcWishlistBootstrap && window.dcWishlistBootstrap.shop,
      window.Shopify && window.Shopify.shop,
      window.shopInfo && window.shopInfo.domain,
      document.querySelector('meta[name="shop"]') &&
        document.querySelector('meta[name="shop"]').getAttribute('content'),
    ];
    for (let i = 0; i < candidates.length; i++) {
      if (isMyshopify(candidates[i])) return candidates[i].trim().toLowerCase();
    }
    return '';
  }

  function getCustomerId() {
    if (window.dcWishlistBootstrap && window.dcWishlistBootstrap.customerId) {
      return String(window.dcWishlistBootstrap.customerId);
    }
    if (window.shopifyCustomer && window.shopifyCustomer.isLoggedIn && window.shopifyCustomer.id) {
      return String(window.shopifyCustomer.id);
    }
    return null;
  }

  function createGuestToken() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function getOrCreateGuestToken() {
    let token = localStorage.getItem(GUEST_TOKEN_KEY);
    if (!isValidGuestToken(token)) {
      token = createGuestToken();
      localStorage.setItem(GUEST_TOKEN_KEY, token);
    }
    return token;
  }

  function migrateInvalidGuestToken() {
    const token = localStorage.getItem(GUEST_TOKEN_KEY);
    if (token && !isValidGuestToken(token)) {
      localStorage.removeItem(GUEST_TOKEN_KEY);
    }
  }

  function parsePrice(raw) {
    if (raw == null || raw === '') return null;
    const n = parseFloat(String(raw).replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  }

  /** Magic Wishlist API rejects relative paths like /cdn/shop/... */
  function normalizeProductImage(raw) {
    if (typeof window.dcNormalizeWishlistImage === 'function') {
      return window.dcNormalizeWishlistImage(raw);
    }
    if (raw == null || raw === '') return null;
    const trimmed = String(raw).trim();
    if (!trimmed) return null;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (trimmed.startsWith('//')) return 'https:' + trimmed;
    if (trimmed.startsWith('/') && typeof window.location !== 'undefined' && window.location.origin) {
      return window.location.origin + trimmed;
    }
    return null;
  }

  function fixDcButtonDataset(button) {
    if (!button || !button.dataset) return;
    const image = normalizeProductImage(button.dataset.productImage);
    if (image) button.dataset.productImage = image;
    else button.removeAttribute('data-product-image');
  }

  function getHandle(button) {
    return (button.dataset.productId || button.dataset.productHandle || '').trim();
  }

  function extractHandlesFromPayload(data) {
    if (!data || typeof data !== 'object') return [];
    if (Array.isArray(data.handles)) return data.handles.filter(Boolean);
    if (Array.isArray(data.items)) {
      return data.items.map((item) => item.productHandle || item.handle || item.productId).filter(Boolean);
    }
    if (data.wishlist && Array.isArray(data.wishlist.items)) {
      return data.wishlist.items
        .map((item) => item.productHandle || item.handle || item.productId)
        .filter(Boolean);
    }
    return [];
  }

  function updateButtonLocal(button, isActive) {
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      isActive
        ? (window.wishlistLanguage && window.wishlistLanguage.removeFromWishlist) || 'Remove from wishlist'
        : (window.wishlistLanguage && window.wishlistLanguage.addToWishlist) || 'Add to wishlist'
    );
  }

  let wishlistHandles = [];

  function parseJsonText(text, contentType) {
    const ct = (contentType || '').toLowerCase();
    const trimmed = (text || '').trim();
    if (ct.includes('json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
      return JSON.parse(trimmed);
    }
    console.error('[DC Wishlist] Expected JSON from app proxy, got HTML. Use shopify theme dev and unlock the store password if enabled.');
    throw new Error('Wishlist API unavailable');
  }

  async function readJsonResponse(response) {
    const text = await response.text();
    try {
      return parseJsonText(text, response.headers.get('content-type'));
    } catch (e) {
      console.error('[DC Wishlist] Response snippet:', text.slice(0, 200));
      throw e;
    }
  }

  function applyHandles(handles) {
    wishlistHandles = [...new Set(handles)];
    if (!window.__wishlistCache) window.__wishlistCache = {};
    window.__wishlistCache.handles = [...wishlistHandles];
    window.__wishlistCache.loaded = true;
  }

  async function loadWishlistHandles() {
    migrateInvalidGuestToken();

    if (window.__wishlistCache && window.__wishlistCache.loaded) {
      const cached = extractHandlesFromPayload(window.__wishlistCache);
      if (cached.length || Array.isArray(window.__wishlistCache.handles)) {
        wishlistHandles = window.__wishlistCache.handles
          ? [...window.__wishlistCache.handles]
          : cached;
        return;
      }
    }

    const shop = getShopDomain();
    if (!shop) {
      console.warn('[DC Wishlist] Missing shop domain.');
      return;
    }

    const customerId = getCustomerId();
    const guest = customerId ? null : getOrCreateGuestToken();
    let url = `/a/magic-wishlist?shop=${encodeURIComponent(shop)}`;
    if (guest) url += `&guestToken=${encodeURIComponent(guest)}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Guest-Token': guest || '',
          'X-Shopify-Customer-Id': customerId || '',
        },
      });
      if (!response.ok) return;
      const data = await readJsonResponse(response);
      applyHandles(extractHandlesFromPayload(data));
    } catch (e) {
      console.warn('[DC Wishlist] Could not load wishlist:', e.message || e);
    }
  }

  function syncAppAndUi() {
    if (window.wishlistApp) {
      window.wishlistApp.wishlist = [...wishlistHandles];
      if (typeof window.wishlistApp.updateAllButtons === 'function') {
        window.wishlistApp.updateAllButtons();
      }
    }
    document.querySelectorAll(DC_BUTTON_SELECTOR).forEach((button) => {
      fixDcButtonDataset(button);
      const handle = getHandle(button);
      if (handle) updateButtonLocal(button, wishlistHandles.includes(handle));
    });
  }

  async function postWishlist(body, customerId, guestToken) {
    const response = await fetch('/a/magic-wishlist', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Guest-Token': guestToken || '',
        'X-Shopify-Customer-Id': customerId || '',
      },
      body: JSON.stringify(body),
    });
    const result = await readJsonResponse(response);
    if (!response.ok) {
      throw new Error((result && result.error) || 'Wishlist API ' + response.status);
    }
    return result;
  }

  async function toggleWishlist(button, retryAfterTokenReset) {
    const handle = getHandle(button);
    if (!handle || button.classList.contains('is-loading')) return;

    const shop = getShopDomain();
    if (!shop) {
      console.warn('[DC Wishlist] Missing shop domain');
      return;
    }

    button.classList.add('is-loading');
    const customerId = getCustomerId();
    let guestToken = customerId ? null : getOrCreateGuestToken();
    const wasIn = wishlistHandles.includes(handle);
    const isAdding = !wasIn;

    if (isAdding) wishlistHandles.push(handle);
    else wishlistHandles = wishlistHandles.filter((h) => h !== handle);
    updateButtonLocal(button, isAdding);

    const locale =
      (window.dcWishlistBootstrap && window.dcWishlistBootstrap.locale) ||
      (window.Shopify && window.Shopify.locale) ||
      'en';

    fixDcButtonDataset(button);
    const image = normalizeProductImage(button.dataset.productImage);

    const body = {
      action: isAdding ? 'add' : 'remove',
      productId: handle,
      shop,
      customerId: customerId || null,
      guestToken,
      locale,
      title: button.dataset.productTitle || '',
      price: parsePrice(button.dataset.productPrice),
      variantId: button.dataset.variantId ? String(button.dataset.variantId) : null,
      ...(isAdding ? {} : { removeAllVariants: true }),
      ...(image ? { image } : {}),
    };

    try {
      const result = await postWishlist(body, customerId, guestToken);

      if (result && result.guestToken && !customerId && isValidGuestToken(result.guestToken)) {
        localStorage.setItem(GUEST_TOKEN_KEY, result.guestToken);
      }

      applyHandles(wishlistHandles);
      window.__wishlistCache.dirty = true;
      syncAppAndUi();
      window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { handle, isAdding } }));

      if (typeof window.__wishlistShowToast === 'function') {
        window.__wishlistShowToast({
          mode: isAdding ? 'add' : 'remove',
          title: body.title || handle,
          image: body.image || '',
        });
      }
    } catch (err) {
      const message = err.message || String(err);
      const invalidGuest =
        !customerId &&
        !retryAfterTokenReset &&
        /guest token/i.test(message);

      if (invalidGuest) {
        localStorage.removeItem(GUEST_TOKEN_KEY);
        if (isAdding) wishlistHandles = wishlistHandles.filter((h) => h !== handle);
        else wishlistHandles.push(handle);
        updateButtonLocal(button, wasIn);
        button.classList.remove('is-loading');
        return toggleWishlist(button, true);
      }

      console.error('[DC Wishlist] Toggle failed:', message);
      if (isAdding) wishlistHandles = wishlistHandles.filter((h) => h !== handle);
      else wishlistHandles.push(handle);
      updateButtonLocal(button, wasIn);
    } finally {
      button.classList.remove('is-loading');
    }
  }

  function findDcButton(target) {
    if (!target || !target.closest) return null;
    const container = target.closest('.dc-wishlist-heart-container');
    if (container) {
      return container.querySelector('.wishlist-heart-btn');
    }
    const button = target.closest('.wishlist-heart-btn');
    if (!button || !button.closest('.dc-wishlist-heart-container')) return null;
    return button;
  }

  /**
   * Stop card/image links from receiving bubbled events.
   * Do NOT preventDefault here — that suppresses the click event on the heart button.
   */
  function shieldFromCardLinks(e) {
    if (!findDcButton(e.target)) return;
    e.stopPropagation();
  }

  let themeClickFallbackBound = false;

  function onThemeWishlistClick(e) {
    const button = findDcButton(e.target);
    if (!button) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    toggleWishlist(button, false);
  }

  function bindThemeClickFallback() {
    if (themeClickFallbackBound) return;
    themeClickFallbackBound = true;
    document.addEventListener('click', onThemeWishlistClick, true);
  }

  function unbindThemeClickFallback() {
    if (!themeClickFallbackBound) return;
    themeClickFallbackBound = false;
    document.removeEventListener('click', onThemeWishlistClick, true);
  }

  function bindInteraction() {
    ['mousedown', 'touchstart'].forEach((type) => {
      const opts = type === 'touchstart' ? { capture: true, passive: true } : true;
      document.addEventListener(type, shieldFromCardLinks, opts);
    });
  }

  function adoptAppWishlistState() {
    if (!window.wishlistApp) return;
    unbindThemeClickFallback();
    if (Array.isArray(window.wishlistApp.wishlist) && window.wishlistApp.wishlist.length) {
      wishlistHandles = [...new Set([...wishlistHandles, ...window.wishlistApp.wishlist])];
      applyHandles(wishlistHandles);
    }
    syncAppAndUi();
  }

  function fixAllDcButtonDatasets() {
    document.querySelectorAll(DC_BUTTON_SELECTOR).forEach(fixDcButtonDataset);
  }

  async function init() {
    fixAllDcButtonDatasets();
    await loadWishlistHandles();
    syncAppAndUi();

    if (window.wishlistApp) {
      adoptAppWishlistState();
      return;
    }

    bindThemeClickFallback();

    let attempts = 0;
    const poll = window.setInterval(() => {
      attempts += 1;
      if (window.wishlistApp) {
        window.clearInterval(poll);
        adoptAppWishlistState();
      } else if (attempts > 40) {
        window.clearInterval(poll);
      }
    }, 250);
  }

  migrateInvalidGuestToken();
  bindInteraction();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('wishlist:updated', () => {
    if (window.__wishlistCache) {
      const fromCache = window.__wishlistCache.handles || extractHandlesFromPayload(window.__wishlistCache);
      if (fromCache.length) applyHandles(fromCache);
    }
    syncAppAndUi();
  });
})();
